/**
 * Concierge + Browse + checkout inside a bounded shell (drawer or
 * approval modal). Keeps the full gift-adjustment funnel in one
 * surface — no /browse navigation, no duplicate escape links.
 */

import { useEffect, useMemo, useState } from "react";
import { useProfile, useCompanyAddresses } from "@/hooks/queries";
import { pickDefaultCompanyAddress } from "@/lib/pickDefaultCompanyAddress";
import { celebrationDateOnly } from "@/lib/orderConfirmPrefill";
import { createPortal } from "react-dom";
import { ArrowLeft } from "lucide-react";
import { InlineConcierge } from "@/components/concierge/InlineConcierge";
import { DrawerBrowsePanel } from "@/components/concierge/DrawerBrowsePanel";
import { ProductCheckoutModals } from "@/components/product-detail/ProductCheckoutModals";
import { useProductCheckoutFlow } from "@/hooks/useProductCheckoutFlow";
import {
  recipientToInitialBrief,
  type DashboardConciergeDrawerRecipient,
} from "@/components/concierge/DashboardConciergeDrawer";
import type { DashboardClientActionHandlers } from "@/lib/dashboardClientActions";
import type { FulfillmentReturnContext } from "@/lib/fulfillmentReturn";
import { humaniseOccasion } from "@/lib/humaniseOccasion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

type FulfillmentTab = "concierge" | "browse";

export interface ConciergeFulfillmentSurfaceProps {
  recipient: DashboardConciergeDrawerRecipient;
  userId: string;
  onDashboardClientActions?: DashboardClientActionHandlers | null;
  fulfillmentReturn?: FulfillmentReturnContext | null;
  /** `modal` enables fullscreen browse overlay inside ApprovalModal. */
  shell?: "drawer" | "modal";
}

export function ConciergeFulfillmentSurface({
  recipient,
  userId,
  onDashboardClientActions = null,
  fulfillmentReturn = null,
  shell = "drawer",
}: ConciergeFulfillmentSurfaceProps) {
  const [activeTab, setActiveTab] = useState<FulfillmentTab>("concierge");

  const profileQuery = useProfile();
  const companyId = profileQuery.data?.company_id ?? null;
  const addressesQuery = useCompanyAddresses(companyId);

  const checkoutDefaultDate = useMemo(
    () => celebrationDateOnly(recipient.celebrationDate),
    [recipient.celebrationDate],
  );
  const checkoutDefaultAddress = useMemo(
    () => pickDefaultCompanyAddress(addressesQuery.data),
    [addressesQuery.data],
  );

  const browseCheckout = useProductCheckoutFlow({ cameFrom: "dashboard" });
  const { resetCheckout: resetBrowseCheckout, handleViewCard: handleBrowseViewCard } =
    browseCheckout;

  const activeDraftScope = useMemo(
    () => ({
      user_id: userId,
      recipient_id: recipient.id,
      ...(recipient.celebrationId ? { celebration_id: recipient.celebrationId } : {}),
    }),
    [userId, recipient],
  );

  const loadActiveDraftFor = useMemo(
    () => ({
      user_id: userId,
      recipient_id: recipient.id,
    }),
    [userId, recipient],
  );

  useEffect(() => {
    resetBrowseCheckout();
  }, [recipient.id, resetBrowseCheckout]);

  const handleTabChange = (tab: FulfillmentTab) => {
    setActiveTab(tab);
  };

  const browseOverlayOpen = activeTab === "browse";
  const browseOverlayZ = shell === "modal" ? "z-[110]" : "z-[60]";

  const occasionLabel = recipient.occasion
    ? humaniseOccasion(recipient.occasion)
    : null;

  const browseFullscreenLayer =
    browseOverlayOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Browse gifts for ${recipient.name}`}
            data-testid="drawer-browse-fullscreen"
            className={`fixed inset-0 ${browseOverlayZ} flex flex-col bg-cream`}
          >
            <div
              className="absolute inset-0 bg-navy/30 backdrop-blur-[2px]"
              aria-hidden="true"
              onClick={() => setActiveTab("concierge")}
            />
            <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden bg-cream shadow-2xl">
              <header
                className="flex shrink-0 items-center justify-between gap-3 border-b bg-white px-4 py-4"
                style={{ borderColor: "rgba(29,52,97,0.08)" }}
              >
                <p className="text-sm font-semibold text-navy">
                  Browse for {recipient.name}
                  {occasionLabel ? ` · ${occasionLabel}` : ""}
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab("concierge")}
                  data-testid="drawer-browse-fullscreen-back"
                  className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:border-coral hover:text-coral"
                >
                  <ArrowLeft size={14} />
                  Concierge
                </button>
              </header>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <DrawerBrowsePanel
                  recipientName={recipient.name}
                  recipientId={recipient.id}
                  occasion={recipient.occasion}
                  dietaryRequirements={recipient.dietaryRequirements}
                  onViewCard={handleBrowseViewCard}
                  layout="fullscreen"
                />
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <Tabs
        value={activeTab}
        onValueChange={(v) => handleTabChange(v as FulfillmentTab)}
        className="flex h-full min-h-0 flex-1 flex-col"
      >
        <TabsList
          className="flex h-auto shrink-0 items-center justify-start gap-1 rounded-none border-b bg-white px-1 py-2"
          style={{ borderColor: "rgba(29,52,97,0.08)" }}
        >
          <TabsTrigger
            value="concierge"
            className="rounded-md px-3 py-1.5 text-[12.5px] font-semibold text-charcoal/65 hover:text-navy data-[state=active]:bg-[rgba(29,52,97,0.06)] data-[state=active]:text-navy data-[state=active]:shadow-none"
          >
            Concierge
          </TabsTrigger>
          <TabsTrigger
            value="browse"
            className="rounded-md px-3 py-1.5 text-[12.5px] font-semibold text-charcoal/65 hover:text-navy data-[state=active]:bg-[rgba(29,52,97,0.06)] data-[state=active]:text-navy data-[state=active]:shadow-none"
          >
            Browse
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="concierge"
          forceMount
          className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden px-0 py-0 data-[state=inactive]:hidden focus-visible:ring-0"
        >
          <InlineConcierge
            key={recipient.id}
            initialBrief={recipientToInitialBrief(recipient)}
            onReset={() => {}}
            cameFrom="dashboard"
            chatSurface="dashboard"
            variant="dashboard"
            celebrationId={recipient.celebrationId ?? null}
            activeDraftScope={activeDraftScope}
            loadActiveDraftFor={loadActiveDraftFor}
            onDashboardClientActions={onDashboardClientActions}
            checkoutDefaultDate={checkoutDefaultDate}
            checkoutDefaultAddress={checkoutDefaultAddress ?? undefined}
          />
        </TabsContent>

        <TabsContent
          value="browse"
          forceMount
          className="mt-0 min-h-0 flex-1 data-[state=inactive]:hidden focus-visible:ring-0"
          aria-hidden={!browseOverlayOpen}
        />
      </Tabs>

      <ProductCheckoutModals
        flow={browseCheckout}
        recipientName={recipient.name}
        recipientId={recipient.id}
        occasion={recipient.occasion ?? "other"}
        defaultDate={checkoutDefaultDate}
        defaultAddress={checkoutDefaultAddress ?? undefined}
        celebrationId={recipient.celebrationId ?? null}
        fulfillmentReturn={fulfillmentReturn}
      />
      {browseFullscreenLayer}
    </div>
  );
}
